import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Chip, IconButton, InputAdornment, ListItemIcon, ListItemText, Menu, MenuItem, Paper, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Tooltip, Typography,
  alpha, useTheme,
} from '@mui/material';
import {
  Add, Search, MoreVert, EditOutlined, DeleteOutline, FileDownloadOutlined, OpenInNew, PictureAsPdfOutlined, Public, Block, Close,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { LibraryDocument } from '../../types';
import { libraryService, LibraryKind } from '../../services/libraryService';
import { centerService } from '../../services/centerService';
import { useAuth } from '../../context/AuthContext';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import { useDebounce } from '../../hooks/useDebounce';
import { useTimezone } from '../../hooks/useTimezone';
import { formatFileSize } from '../../utils/fileSize';
import FilterBar from '../common/FilterBar';
import EmptyState from '../common/EmptyState';
import TableRowSkeletons from '../common/TableRowSkeletons';
import ConfirmationDialog from '../common/ConfirmationDialog';
import BusyIcon from '../common/BusyIcon';
import LibraryDocumentDialog, { LibraryDocumentInput } from './LibraryDocumentDialog';

export interface LibraryConfig {
  kind: LibraryKind;
  /** The permission module — "policies", "sops". */
  module: string;
  title: string;
  subtitle: string;
  /** One of them, in a sentence — "policy", "SOP". */
  noun: string;
  /** More than one — "policies", "SOPs". */
  plural: string;
}

/**
 * Policies and SOPs: the school's PDFs for its staff — search, read in a new
 * tab, download, and (with write access) upload, rename or remove. Global
 * ones, set by the platform, appear in every school marked "All schools".
 */
const LibraryPage: React.FC<{ config: LibraryConfig }> = ({ config }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const { canRead, canWrite } = useModulePermissions();
  const { formatDate } = useTimezone();
  const service = useMemo(() => libraryService(config.kind), [config.kind]);
  const writable = canWrite(config.module);
  const systemAdmin = !!user?.isSystemAdmin;

  const [items, setItems] = useState<LibraryDocument[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const search = useDebounce(q.trim(), 300);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [schools, setSchools] = useState<Array<{ value: string; label: string }>>([]);
  const [dialog, setDialog] = useState<{ editing: LibraryDocument | null } | null>(null);
  const [menu, setMenu] = useState<{ anchor: HTMLElement; doc: LibraryDocument } | null>(null);
  const [removing, setRemoving] = useState<LibraryDocument | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await service.list({ page: page + 1, limit, search: search || undefined });
      setItems(r.items);
      setTotal(r.pagination.totalItems);
    } catch (e: any) {
      enqueueSnackbar(e?.message || `Could not load the ${config.plural}`, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [service, page, limit, search, enqueueSnackbar, config.plural]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(0); }, [search]);
  useEffect(() => {
    if (!systemAdmin) return;
    centerService.getCenters().then(r => setSchools((r?.centers ?? []).map((s: any) => ({ value: s._id, label: s.name }))));
  }, [systemAdmin]);

  const save = async (input: LibraryDocumentInput) => {
    const editing = dialog?.editing;
    try {
      if (editing) {
        await service.update(editing._id, { title: input.title, ...(systemAdmin ? { isGlobal: input.isGlobal } : {}) });
        enqueueSnackbar(`Renamed to “${input.title}”`, { variant: 'success' });
      } else {
        await service.create({
          title: input.title, file: input.file!,
          ...(systemAdmin ? { isGlobal: input.isGlobal, schoolId: input.isGlobal ? undefined : input.schoolId } : {}),
        });
        enqueueSnackbar(`Uploaded “${input.title}”`, { variant: 'success' });
      }
      setDialog(null);
      load();
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Could not save', { variant: 'error' });
    }
  };

  const remove = async () => {
    if (!removing) return;
    try {
      await service.remove(removing._id);
      enqueueSnackbar(`Removed “${removing.title}”`, { variant: 'success' });
      setRemoving(null);
      if (items.length === 1 && page > 0) setPage(p => p - 1); else load();
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Could not remove it', { variant: 'error' });
    }
  };

  const run = async (key: string, task: () => Promise<void>) => {
    setBusy(key);
    try { await task(); } catch (e: any) { enqueueSnackbar(e?.message || 'The file could not be opened', { variant: 'error' }); } finally { setBusy(null); }
  };

  if (!canRead(config.module)) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <EmptyState icon={<Block />} title="No access" description={`You don't have permission to view ${config.plural}. Ask your administrator for access.`} />
      </Box>
    );
  }

  const columns = systemAdmin ? 6 : 5;
  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'flex-start' }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>{config.title}</Typography>
          <Typography variant="body2" color="text.secondary">{config.subtitle}</Typography>
        </Box>
        {writable && (
          <Button variant="contained" disableElevation startIcon={<Add />} onClick={() => setDialog({ editing: null })}>
            Upload {config.noun}
          </Button>
        )}
      </Stack>

      <FilterBar loading={loading && items.length > 0}>
        <TextField
          size="small"
          placeholder={`Search ${config.plural}`}
          value={q}
          onChange={e => setQ(e.target.value)}
          sx={{ width: { xs: '100%', sm: 320 } }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
            endAdornment: q ? <InputAdornment position="end"><IconButton size="small" onClick={() => setQ('')} aria-label="Clear search"><Close fontSize="small" /></IconButton></InputAdornment> : null,
          }}
        />
        <Box sx={{ flex: 1 }} />
        {!loading && (
          <Typography variant="body2" color="text.secondary">
            {total} {total === 1 ? config.noun : config.plural}{search ? ` matching “${search}”` : ''}
          </Typography>
        )}
      </FilterBar>

      {!loading && items.length === 0 ? (
        <Paper variant="outlined" sx={{ borderRadius: 2.5 }}>
          <EmptyState
            icon={<PictureAsPdfOutlined />}
            title={search ? `No ${config.plural} match “${search}”` : `No ${config.plural} yet`}
            description={search ? 'Try another word, or clear the search.' : `Upload a PDF and it appears here for your staff to read and download.`}
            action={search
              ? { label: 'Clear search', onClick: () => setQ('') }
              : writable ? { label: `Upload ${config.noun}`, onClick: () => setDialog({ editing: null }), icon: <Add /> } : undefined}
          />
        </Paper>
      ) : (
        <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Document</TableCell>
                  {systemAdmin && <TableCell sx={{ fontWeight: 600 }}>School</TableCell>}
                  <TableCell sx={{ fontWeight: 600 }}>Uploaded by</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Uploaded</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Size</TableCell>
                  <TableCell sx={{ width: 140 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && items.length === 0 ? <TableRowSkeletons columns={columns} rows={5} /> : items.map(doc => (
                  <TableRow key={doc._id} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box sx={{ width: 36, height: 36, flexShrink: 0, borderRadius: 1.5, display: 'grid', placeItems: 'center', bgcolor: alpha(theme.palette.error.main, 0.08), color: 'error.main' }}>
                          <PictureAsPdfOutlined fontSize="small" />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              noWrap
                              onClick={() => run(`open:${doc._id}`, () => service.open(doc))}
                              sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                            >
                              {doc.title}
                            </Typography>
                            {doc.isGlobal && <Chip size="small" icon={<Public sx={{ fontSize: 14 }} />} label="All schools" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }} />}
                          </Stack>
                          <Typography variant="caption" color="text.secondary" noWrap component="div">{doc.fileName}</Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    {systemAdmin && <TableCell><Typography variant="body2">{doc.isGlobal ? '—' : doc.schoolId?.name ?? '—'}</Typography></TableCell>}
                    <TableCell><Typography variant="body2">{doc.uploadedBy?.name ?? '—'}</Typography></TableCell>
                    <TableCell><Typography variant="body2" noWrap>{formatDate(doc.createdAt)}</Typography></TableCell>
                    <TableCell align="right"><Typography variant="body2" color="text.secondary" noWrap>{formatFileSize(doc.fileSize)}</Typography></TableCell>
                    <TableCell align="right" sx={{ pr: 1 }}>
                      <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                        <Tooltip title="Open">
                          <span><IconButton size="small" onClick={() => run(`open:${doc._id}`, () => service.open(doc))} disabled={busy === `open:${doc._id}`} aria-label="Open">
                            <BusyIcon busy={busy === `open:${doc._id}`}><OpenInNew fontSize="small" /></BusyIcon>
                          </IconButton></span>
                        </Tooltip>
                        <Tooltip title="Download">
                          <span><IconButton size="small" onClick={() => run(`dl:${doc._id}`, () => service.download(doc))} disabled={busy === `dl:${doc._id}`} aria-label="Download">
                            <BusyIcon busy={busy === `dl:${doc._id}`}><FileDownloadOutlined fontSize="small" /></BusyIcon>
                          </IconButton></span>
                        </Tooltip>
                        {writable && (
                          <IconButton size="small" onClick={e => setMenu({ anchor: e.currentTarget, doc })} aria-label="More">
                            <MoreVert fontSize="small" />
                          </IconButton>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {total > 10 && (
            <TablePagination
              component="div"
              count={total}
              page={page}
              rowsPerPage={limit}
              rowsPerPageOptions={[10, 25, 50]}
              onPageChange={(_, p) => setPage(p)}
              onRowsPerPageChange={e => { setLimit(parseInt(e.target.value, 10)); setPage(0); }}
              sx={{ borderTop: 1, borderColor: 'divider' }}
            />
          )}
        </Paper>
      )}

      <Menu
        anchorEl={menu?.anchor}
        open={!!menu}
        onClose={() => setMenu(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 180, borderRadius: 2 } } }}
      >
        <MenuItem onClick={() => { setDialog({ editing: menu!.doc }); setMenu(null); }}>
          <ListItemIcon><EditOutlined fontSize="small" /></ListItemIcon>
          <ListItemText primary="Rename" />
        </MenuItem>
        <MenuItem onClick={() => { setRemoving(menu!.doc); setMenu(null); }} sx={{ color: 'error.main' }}>
          <ListItemIcon><DeleteOutline fontSize="small" color="error" /></ListItemIcon>
          <ListItemText primary="Remove" />
        </MenuItem>
      </Menu>

      <LibraryDocumentDialog
        open={!!dialog}
        noun={config.noun}
        editing={dialog?.editing ?? null}
        schools={systemAdmin ? schools : undefined}
        onClose={() => setDialog(null)}
        onSave={save}
      />

      <ConfirmationDialog
        open={!!removing}
        onClose={() => setRemoving(null)}
        onConfirm={remove}
        title={`Remove this ${config.noun}?`}
        message={removing ? `“${removing.title}” will no longer be available to your staff.` : ''}
        confirmText="Remove"
        confirmColor="error"
      />
    </Box>
  );
};

export default LibraryPage;
